import { useRef } from 'react';
import type { MouseEventHandler } from 'react';

import get from 'lodash/get';

import { withRtlSupport } from '@skyscanner/backpack-web/bpk-component-icon';
import HotelIconSm from '@skyscanner/backpack-web/bpk-component-icon/sm/hotels';
import {
  BpkIconMarker,
  BpkOverlayView,
  BpkPriceMarker,
  MARKER_STATUSES,
} from '@skyscanner/backpack-web/bpk-component-map';
import {
  BpkSpinner,
  SPINNER_TYPES,
} from '@skyscanner/backpack-web/bpk-component-spinner';
import type { SpinnerTypes } from '@skyscanner/backpack-web/bpk-component-spinner/src/spinnerTypes';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import MapMarkerTarget from '../../common/MapMarkerTarget';
import { withI18n } from '../../contexts/i18n';
import MiniCard from '../MiniCard';

import MapTooltip from './MapTooltip';

import type { Status } from '../../common/MapPriceMarkerButton/MapPriceMarkerButton';
import type { Position, HotelCard } from '@shared/types/frontend/hotel-card';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Nullable, Stay } from '@shared/types/utils';

import STYLES from './HotelMapMarker.module.scss';

import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import keyFactory from '@shared/utils-frontend/key-factory';
import { centeredPositionOffset } from '@shared/utils-frontend/overlay-view-position-offset';

type Props = {
  i18n: I18nService;
  userContext: UserContext;
  id?: string;
  position: Position;
  hotelCardPosition?: number;
  selected?: boolean;
  viewed?: boolean;
  buttonProps?: Object;
  showPrice?: boolean;
  isMobile?: boolean;
  className?: string;
  hotelName?: string;
  priceType?: Nullable<string>;
  pricePolicy?: string;
  stay?: Stay;
  hotelCard?: HotelCard;
  onClick?: MouseEventHandler;
  onHotelCardClicked?: (params: any) => void;
};

const AlignedHotelIconSm = withRtlSupport(HotelIconSm);

const cls = cssModules(STYLES);

const HotelMapMarker = ({
  buttonProps,
  className,
  hotelCard,
  hotelCardPosition = 0,
  hotelName,
  i18n,
  id,
  isMobile,
  onClick,
  onHotelCardClicked,
  position,
  priceType,
  selected,
  showPrice,
  viewed,
}: Props) => {
  const target = useRef(null);
  const loading = get(hotelCard, 'priceInfo.loading');
  const price = get(hotelCard, 'priceInfo.price');

  let status = 'default';
  let spinnerType: SpinnerTypes = SPINNER_TYPES.light;
  if (selected) {
    status = 'selected';
    spinnerType = SPINNER_TYPES.primary;
  } else if (!price && !loading) {
    status = 'soldout';
  } else if (viewed) {
    status = 'viewed';
    spinnerType = SPINNER_TYPES.primary;
  }

  const getMarkerVariantStatus = (): Status => {
    if (selected) {
      return MARKER_STATUSES.selected;
    }
    return MARKER_STATUSES.unselected;
  };

  if (showPrice) {
    let priceText: Nullable<string> = null;
    if (price) {
      priceText = i18n.formatCurrency(price);
    } else if (!loading) {
      priceText = i18n.translate('OTARates_label_unavailable');
    }

    const markerClassName = cls(
      'HotelMapMarker__price',
      `HotelMapMarker--${status}`,
    );

    if (isMobile) {
      return (
        <BpkPriceMarker
          key={id}
          label={priceText}
          accessibilityLabel={priceText}
          position={position}
          status={getMarkerVariantStatus()}
          onClick={onClick}
          buttonProps={buttonProps}
          className={cls(
            'HotelMapMarker__markerText',
            selected && `HotelMapMarker__pinSelected`,
          )}
        />
      );
    }

    const markerButton = (
      <button
        type="button"
        className={cls(
          'HotelMapMarker__button',
          selected && `HotelMapMarker__button--selected`,
        )}
        onClick={onClick}
        {...buttonProps}
      >
        <div className={markerClassName}>
          <span>
            {loading && <BpkSpinner type={spinnerType} alignToButton={false} />}
          </span>
          <BpkText
            textStyle={TEXT_STYLES.label1}
            className={cls(loading ? 'HotelMapMarker__loading' : null)}
          >
            <span>{priceText}</span>
          </BpkText>
        </div>
      </button>
    );
    const renderTargetId = `HotelMapMarker-tooltipContent-${id}`;

    return (
      <BpkOverlayView
        position={position}
        getPixelPositionOffset={centeredPositionOffset}
      >
        <div
          className={cls('HotelMapMarker__tooltipContent')}
          id={renderTargetId}
        >
          {!hotelCard ? (
            markerButton
          ) : (
            <MapTooltip
              target={
                <span ref={target}>
                  <MapMarkerTarget
                    priceText={priceText}
                    id={id}
                    hotelCard={hotelCard}
                    onClick={onClick}
                    mapRefreshStatus={getMarkerVariantStatus()}
                    selected={selected}
                    hotelCardPosition={hotelCardPosition}
                  />
                </span>
              }
              portalClassName={STYLES.HotelMapMarker__tooltip}
              renderTarget={() => document.getElementById(renderTargetId)}
            >
              <div className={STYLES.HotelMapMarker__hotelCard}>
                <MiniCard
                  hotel={hotelCard}
                  onClick={onHotelCardClicked}
                  position={hotelCardPosition}
                  showPrice
                  isVertical
                  priceType={priceType}
                />
              </div>
            </MapTooltip>
          )}
        </div>
      </BpkOverlayView>
    );
  }

  return (
    <BpkIconMarker
      key={keyFactory.next()}
      className={cls('HotelMapMarker', className)}
      icon={<AlignedHotelIconSm />}
      selected={selected}
      position={position}
      buttonProps={{ ...buttonProps, title: hotelName }}
    />
  );
};

export default withI18n(withUserContext(HotelMapMarker));
